import React, { useState } from 'react';
import { Server, Zap, Shield, Activity, ChevronUp, ChevronDown, Cctv, Network } from 'lucide-react';

interface AssetCount {
    total: number;
    online: number;
    offline: number;
}

interface AssetStatsProps {
    servers: AssetCount;
    pdus: AssetCount;
    cameras: AssetCount;
    chillers: AssetCount;
    generators: AssetCount;
    switches: AssetCount;
}

const StatCard: React.FC<{
    icon: React.FC<any>;
    title: string;
    stats: AssetCount;
    colorHex: string;
    bgClass: string;
}> = ({ icon: Icon, title, stats, colorHex, bgClass }) => (
    <div className={`p-3 rounded-xl border border-white/5 ${bgClass} w-[138px] transition-all hover:bg-white/5 duration-300`}>
        <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-md bg-white/10 shrink-0">
                <Icon size={14} style={{ color: colorHex }} />
            </div>
            <h4 className="text-[11px] font-black text-gray-100 uppercase tracking-tight truncate">{title}</h4>
        </div>

        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <span className="text-[9px] font-bold text-gray-500 uppercase">Total:</span>
                <span className="text-[11px] font-black text-white leading-none">{stats.total}</span>
            </div>

            <div className="flex items-center justify-between">
                <span className="text-[9px] font-bold text-emerald-500/70 uppercase flex items-center gap-1 shrink-0">
                    <span className="w-1 h-1 bg-emerald-500 rounded-full" /> Online:
                </span>
                <span className="text-[11px] font-black text-emerald-400 leading-none">{stats.online}</span>
            </div>

            <div className="flex items-center justify-between">
                <span className="text-[9px] font-bold text-red-500/70 uppercase flex items-center gap-1 shrink-0">
                    <span className="w-1 h-1 bg-red-500 rounded-full" /> Offline:
                </span>
                <span className="text-[11px] font-black text-red-400 leading-none">{stats.offline}</span>
            </div>
        </div>
    </div>
);

export default function AssetStats({
    servers,
    pdus,
    cameras,
    chillers,
    generators,
    switches
}: AssetStatsProps) {
    const [collapsed, setCollapsed] = useState(false);

    return (
        <div className="flex flex-col items-center">
            <div
                className={`transition-all duration-500 ease-in-out overflow-hidden ${
                    collapsed ? 'max-h-0 opacity-0' : 'max-h-[180px] opacity-100'
                }`}
            >
                <div className="bg-[#0b1120]/80 backdrop-blur-xl border border-white/5 p-3 rounded-b-2xl shadow-2xl flex gap-3">
                    <StatCard
                        icon={Server}
                        title="Servers"
                        stats={servers}
                        colorHex="#3b82f6"
                        bgClass="bg-blue-500/5"
                    />
                    <StatCard
                        icon={Network}
                        title="Switches"
                        stats={switches}
                        colorHex="#6366f1"
                        bgClass="bg-indigo-500/5"
                    />
                    <StatCard
                        icon={Zap}
                        title="PDUs"
                        stats={pdus}
                        colorHex="#eab308"
                        bgClass="bg-yellow-500/5"
                    />
                    <StatCard
                        icon={Cctv}
                        title="Cameras"
                        stats={cameras}
                        colorHex="#a855f7"
                        bgClass="bg-purple-500/5"
                    />
                    <StatCard
                        icon={Activity}
                        title="Chillers"
                        stats={chillers}
                        colorHex="#22d3ee"
                        bgClass="bg-cyan-500/5"
                    />
                    <StatCard
                        icon={Shield}
                        title="Generators"
                        stats={generators}
                        colorHex="#f97316"
                        bgClass="bg-orange-500/5"
                    />
                </div>
            </div>

            <button
                onClick={() => setCollapsed(!collapsed)}
                className="mt-[-1px] bg-[#0b1120]/80 backdrop-blur-xl border border-t-0 border-white/5 px-4 py-1 rounded-b-lg flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase tracking-[0.14em] hover:text-white transition-colors"
            >
                {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                {collapsed ? 'Stats' : 'Close'}
            </button>
        </div>
    );
}